import {useCallback} from 'react';
import {redirectWithForwardUrl} from 'utils/utils';

import {useGetForwardUrl} from './useGetFowardUrl';
import {AdobeEvent, useTrackEvent} from './useTrackEvent';

const DISMISS_FALLBACK_URL = '/rewards/home';

/**
 * URLs matching these patterns require MFA step-up to load, so redirecting
 * to them on dismiss would create an infinite verification loop.
 */
const CIRCULAR_REDIRECT_PATTERNS = [/\/asp_net\/universalpa\.aspx/i];

/**
 * Returns true when the given forwardUrl would redirect back into
 * an MFA-protected flow, causing a circular redirect loop.
 */
export const isCircularForwardUrl = (url: string): boolean => {
  if (!url) return false;

  // Decode the URL in case it's percent-encoded (e.g. %2fASP_Net%2funiversalpa.aspx)
  let decoded: string;
  try {
    decoded = decodeURIComponent(url);
  } catch {
    decoded = url;
  }

  return CIRCULAR_REDIRECT_PATTERNS.some((pattern) => pattern.test(decoded));
};

export const useDismissBehavior = (trackingEventName?: string) => {
  const forwardUrl = useGetForwardUrl() || '';

  const {trackAdobeEvent} = useTrackEvent();

  return useCallback(() => {
    if (trackingEventName) {
      trackAdobeEvent({
        event: AdobeEvent.click,
        click: {
          name: trackingEventName,
        },
      });
    }

    // If the forwardUrl would loop back into step-up, redirect to a safe fallback instead
    const redirectTarget = isCircularForwardUrl(forwardUrl)
      ? DISMISS_FALLBACK_URL
      : forwardUrl;

    redirectWithForwardUrl(redirectTarget);
  }, [forwardUrl, trackAdobeEvent, trackingEventName]);
};
